import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Truck, X, ChevronDown, ChevronUp } from 'lucide-react';
import { motoristasService } from '../../services/motoristasService';
import { useOrganizador } from '../../contexts/OrganizadorContext';
import { useToast } from '../../hooks/useToast';
import Toast from '../../components/ui/Toast';

const UFS = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
  'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
];

const FORM_VAZIO = { nome: '', telefone: '', cidade: '', uf: '' };
const CAMINHAO_VAZIO = { placa: '', uf: '' };

const MotoristasList = () => {
  const { organizadorAtivo } = useOrganizador();
  const { toasts, success, error, hideToast } = useToast();

  const [motoristas, setMotoristas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandido, setExpandido] = useState(null);

  // Modal motorista
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [modalPlaca, setModalPlaca] = useState('');
  const [modalPlacaUf, setModalPlacaUf] = useState('');
  const [salvando, setSalvando] = useState(false);

  // Confirmação exclusão
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Caminhão inline
  const [novoCaminhao, setNovoCaminhao] = useState({});

  useEffect(() => {
    if (organizadorAtivo?.id) carregar();
  }, [organizadorAtivo?.id]);

  const carregar = async () => {
    try {
      setLoading(true);
      const res = await motoristasService.listar(organizadorAtivo.id);
      setMotoristas(res.data);
    } catch {
      error('Erro ao carregar motoristas');
    } finally {
      setLoading(false);
    }
  };

  const abrirNovo = () => {
    setEditando(null);
    setForm(FORM_VAZIO);
    setModalPlaca('');
    setModalPlacaUf('');
    setShowModal(true);
  };

  const abrirEditar = (m) => {
    setEditando(m);
    setForm({ nome: m.nome, telefone: m.telefone || '', cidade: m.cidade || '', uf: m.uf || '' });
    setShowModal(true);
  };

  const fecharModal = () => { setShowModal(false); setEditando(null); setForm(FORM_VAZIO); setModalPlaca(''); setModalPlacaUf(''); };

  const salvar = async () => {
    if (!form.nome.trim()) { error('Nome é obrigatório'); return; }
    try {
      setSalvando(true);
      if (editando) {
        await motoristasService.atualizar(organizadorAtivo.id, editando.id, form);
        success('Motorista atualizado!');
      } else {
        const res = await motoristasService.criar(organizadorAtivo.id, form);
        // Se informou placa, adicionar caminhão automaticamente
        if (modalPlaca.trim() && res.data?.id) {
          try {
            await motoristasService.adicionarCaminhao(organizadorAtivo.id, res.data.id, { placa: modalPlaca.trim(), uf: modalPlacaUf });
          } catch { /* silencia */ }
        }
        success('Motorista cadastrado!');
      }
      fecharModal();
      await carregar();
    } catch {
      error('Erro ao salvar motorista');
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async () => {
    if (!confirmDelete) return;
    try {
      await motoristasService.excluir(organizadorAtivo.id, confirmDelete.id);
      success('Motorista excluído!');
      setConfirmDelete(null);
      await carregar();
    } catch {
      error('Erro ao excluir motorista');
    }
  };

  const toggleExpandido = (id) => setExpandido(prev => prev === id ? null : id);

  const adicionarCaminhao = async (motoristaId) => {
    const cam = novoCaminhao[motoristaId] || CAMINHAO_VAZIO;
    if (!cam.placa.trim()) { error('Informe a placa'); return; }
    try {
      await motoristasService.adicionarCaminhao(organizadorAtivo.id, motoristaId, cam);
      setNovoCaminhao(prev => ({ ...prev, [motoristaId]: CAMINHAO_VAZIO }));
      success('Caminhão adicionado!');
      await carregar();
    } catch {
      error('Erro ao adicionar caminhão');
    }
  };

  const removerCaminhao = async (motoristaId, caminhaoId) => {
    try {
      await motoristasService.removerCaminhao(organizadorAtivo.id, motoristaId, caminhaoId);
      success('Caminhão removido!');
      await carregar();
    } catch {
      error('Erro ao remover caminhão');
    }
  };

  return (
    <>
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 space-y-2">
        {toasts.map(t => (
          <Toast key={t.id} message={t.message} type={t.type} duration={t.duration} onClose={() => hideToast(t.id)} />
        ))}
      </div>

      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Motoristas</h2>
          <button onClick={abrirNovo} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Novo Motorista
          </button>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Lista de Motoristas</h3>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Carregando...</div>
            ) : motoristas.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Nenhum motorista cadastrado</div>
            ) : (
              <div className="space-y-2">
                {motoristas.map(m => (
                  <div key={m.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Linha principal */}
                    <div className="flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <Truck className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 truncate">{m.nome}</div>
                          <div className="text-xs text-gray-500">
                            {[m.telefone, m.cidade && m.uf ? `${m.cidade}/${m.uf}` : m.cidade || m.uf].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        <span className="ml-2 text-xs text-gray-400 flex-shrink-0">
                          {m.caminhoes?.length || 0} caminhão(ões)
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button onClick={() => abrirEditar(m)} className="text-yellow-600 hover:text-yellow-800 p-1">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => setConfirmDelete(m)} className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => toggleExpandido(m.id)} className="text-gray-400 hover:text-gray-700 p-1">
                          {expandido === m.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Painel de caminhões */}
                    {expandido === m.id && (
                      <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Caminhões</p>
                        {(m.caminhoes || []).length === 0 ? (
                          <p className="text-sm text-gray-400 mb-3">Nenhum caminhão cadastrado</p>
                        ) : (
                          <div className="space-y-1 mb-3">
                            {m.caminhoes.map(c => (
                              <div key={c.id} className="flex items-center justify-between bg-white rounded border border-gray-200 px-3 py-1.5">
                                <span className="text-sm font-mono font-semibold text-gray-800">{c.placa}</span>
                                {c.uf && <span className="text-xs text-gray-500 ml-2">{c.uf}</span>}
                                <button onClick={() => removerCaminhao(m.id, c.id)} className="text-red-500 hover:text-red-700 ml-auto">
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Adicionar caminhão */}
                        <div className="flex gap-2">
                          <input
                            className="input-field flex-1 text-sm uppercase"
                            placeholder="Placa"
                            maxLength={8}
                            value={novoCaminhao[m.id]?.placa || ''}
                            onChange={e => setNovoCaminhao(prev => ({
                              ...prev,
                              [m.id]: { ...(prev[m.id] || CAMINHAO_VAZIO), placa: e.target.value.toUpperCase() }
                            }))}
                          />
                          <select
                            className="input-field w-20 text-sm"
                            value={novoCaminhao[m.id]?.uf || ''}
                            onChange={e => setNovoCaminhao(prev => ({
                              ...prev,
                              [m.id]: { ...(prev[m.id] || CAMINHAO_VAZIO), uf: e.target.value }
                            }))}
                          >
                            <option value="">UF</option>
                            {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                          </select>
                          <button
                            onClick={() => adicionarCaminhao(m.id)}
                            className="btn-primary text-sm px-3 py-1.5"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal Motorista */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editando ? 'Editar Motorista' : 'Novo Motorista'}
              </h3>
              <button onClick={fecharModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input
                  className="input-field w-full"
                  value={form.nome}
                  onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                  placeholder="Nome completo"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                <input
                  className="input-field w-full"
                  value={form.telefone}
                  onChange={e => setForm(f => ({ ...f, telefone: e.target.value }))}
                  placeholder="(00) 00000-0000"
                />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                  <input
                    className="input-field w-full"
                    value={form.cidade}
                    onChange={e => setForm(f => ({ ...f, cidade: e.target.value }))}
                    placeholder="Cidade"
                  />
                </div>
                <div className="w-24">
                  <label className="block text-sm font-medium text-gray-700 mb-1">UF</label>
                  <select
                    className="input-field w-full"
                    value={form.uf}
                    onChange={e => setForm(f => ({ ...f, uf: e.target.value }))}
                  >
                    <option value="">—</option>
                    {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                  </select>
                </div>
              </div>
              {!editando && (
                <div className="border-t border-gray-200 pt-3 mt-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Placa do Caminhão</label>
                  <div className="flex gap-3">
                    <input
                      className="input-field flex-1"
                      value={modalPlaca}
                      onChange={e => setModalPlaca(e.target.value.toUpperCase())}
                      placeholder="ABC-1234"
                      maxLength={8}
                    />
                    <div className="w-24">
                      <select
                        className="input-field w-full"
                        value={modalPlacaUf}
                        onChange={e => setModalPlacaUf(e.target.value)}
                      >
                        <option value="">UF</option>
                        {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={fecharModal} className="btn-secondary">Cancelar</button>
              <button onClick={salvar} disabled={salvando} className="btn-primary">
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmação exclusão */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Excluir Motorista</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir <strong>{confirmDelete.nome}</strong>?
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="btn-secondary">Cancelar</button>
              <button onClick={excluir} className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700">
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MotoristasList;

// Exporta modal de criação rápida para uso na Liberação Curral
export const MotoristaRapidoModal = ({ isOpen, onClose, onSave, organizadorId }) => {
  const [form, setForm] = useState(FORM_VAZIO);
  const [placa, setPlaca] = useState('');
  const [placaUf, setPlacaUf] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => { if (!isOpen) { setForm(FORM_VAZIO); setPlaca(''); setPlacaUf(''); } }, [isOpen]);

  if (!isOpen) return null;

  const salvar = async () => {
    if (!form.nome.trim()) return;
    try {
      setSalvando(true);
      const res = await motoristasService.criar(organizadorId, form);
      const motorista = res.data;
      
      // Se informou placa, adicionar caminhão automaticamente
      if (placa.trim()) {
        try {
          await motoristasService.adicionarCaminhao(organizadorId, motorista.id, { placa: placa.trim(), uf: placaUf });
        } catch { /* silencia — motorista já foi criado */ }
      }
      
      onSave(motorista);
      onClose();
    } catch {
      // silencia — pai trata
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-medium text-gray-900">Novo Motorista</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <input
            className="input-field w-full"
            placeholder="Nome *"
            value={form.nome}
            onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
          />
          <input
            className="input-field w-full"
            placeholder="Telefone"
            value={form.telefone}
            onChange={e => setForm(f => ({ ...f, telefone: e.target.value }))}
          />
          <div className="flex gap-2">
            <input
              className="input-field flex-1"
              placeholder="Cidade"
              value={form.cidade}
              onChange={e => setForm(f => ({ ...f, cidade: e.target.value }))}
            />
            <select
              className="input-field w-20"
              value={form.uf}
              onChange={e => setForm(f => ({ ...f, uf: e.target.value }))}
            >
              <option value="">UF</option>
              {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
            </select>
          </div>
          <div className="border-t border-gray-200 pt-3 mt-3">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Placa do Caminhão</label>
            <div className="flex gap-2">
              <input
                className="input-field flex-1"
                placeholder="ABC-1234"
                value={placa}
                onChange={e => setPlaca(e.target.value.toUpperCase())}
                maxLength={8}
              />
              <select
                className="input-field w-20"
                value={placaUf}
                onChange={e => setPlacaUf(e.target.value)}
              >
                <option value="">UF</option>
                {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="btn-secondary text-sm">Cancelar</button>
          <button onClick={salvar} disabled={salvando || !form.nome.trim()} className="btn-primary text-sm">
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
};
